import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../context/AuthContext'
import { toast } from 'react-toastify'
import { Eye, EyeOff, Mail, Lock, ArrowRight, Zap, ShieldCheck, Globe, TrendingUp } from 'lucide-react'

const features = [
  { icon: <ShieldCheck size={17} />, text: 'Bank-grade security & encryption' },
  { icon: <Globe size={17} />, text: 'Multi-currency payment gateway' },
  { icon: <TrendingUp size={17} />, text: 'Real-time analytics & reporting' },
]

const Login = () => {
  const navigate = useNavigate()
  const { login, user, isAuthenticated } = useAuth()
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPw, setShowPw] = useState(false)

  useEffect(() => {
    if (isAuthenticated()) {
      if (user?.role === 'admin') navigate('/admin/dashboard', { replace: true })
      else if (user?.role === 'business_partner') navigate('/bp/dashboard', { replace: true })
      else if (user?.account_status === 'active') navigate('/user/dashboard', { replace: true })
      else navigate('/user/pending', { replace: true })
    }
  }, [user, isAuthenticated, navigate])

  const onChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }))
    if (error) setError('')
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.email.trim() || !form.password.trim()) { setError('Email and password are required'); return }
    setLoading(true); setError('')
    try {
      const res = await fetch('https://api.a1xpay.com/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: form.email.trim(), password: form.password }),
      })
      const data = await res.json()
      if (!res.ok || !data.success || !data.user || !data.token) throw new Error(data.message || 'Login failed')
      login(data.user, data.token)
      toast.success('Welcome back!')
      setTimeout(() => {
        let path = '/user/pending'
        if (data.user.role === 'admin') path = '/admin/dashboard'
        else if (data.user.role === 'business_partner') path = '/bp/dashboard'
        else if (data.user.account_status === 'active') path = '/user/dashboard'
        navigate(path, { replace: true })
      }, 200)
    } catch (err) {
      const msg = err.message === 'Failed to fetch' ? 'Cannot reach server. Please try again.' : err.message
      setError(msg); toast.error(msg)
    } finally { setLoading(false) }
  }

  return (
    <>
      <style>{css}</style>
      <div className="auth-page">
        {/* LEFT PANEL */}
        <div className="auth-left">
          <div className="auth-left-inner">
            <Link to="/" className="auth-brand">
              <div className="auth-brand-icon"><Zap size={18} strokeWidth={2.5} /></div>
              <span>A1X<span className="auth-brand-accent">PAY</span></span>
            </Link>
            <motion.div className="auth-left-content"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}>
              <div className="auth-eyebrow">Trusted by 200+ businesses</div>
              <h2 className="auth-left-heading">The payment platform for modern businesses</h2>
              <p className="auth-left-sub">Secure, scalable, lightning-fast payment infrastructure — from startup to enterprise.</p>
              <div className="auth-features">
                {features.map((f, i) => (
                  <motion.div key={i} className="auth-feature"
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}>
                    <div className="auth-feature-icon">{f.icon}</div>
                    <span>{f.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
          <div className="auth-blob auth-blob-1" />
          <div className="auth-blob auth-blob-2" />
        </div>

        {/* RIGHT — floating card */}
        <div className="auth-right">
          <motion.div className="auth-card"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}>

            <div className="auth-card-header">
              <h1 className="auth-card-title">Sign in</h1>
              <p className="auth-card-sub">Welcome back to A1XPAY</p>
            </div>

            {error && (
              <motion.div className="auth-error" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                {error}
              </motion.div>
            )}

            <form onSubmit={onSubmit}>
              <div className="auth-field">
                <label className="auth-label">Email address</label>
                <div className="auth-input-wrap">
                  <Mail size={14} className="auth-input-icon" />
                  <input type="email" name="email" value={form.email} onChange={onChange}
                    placeholder="you@company.com" className="auth-input"
                    disabled={loading} autoComplete="email" required />
                </div>
              </div>

              <div className="auth-field">
                <div className="auth-label-row">
                  <label className="auth-label">Password</label>
                  <Link to="/forgot-password" className="auth-forgot">Forgot password?</Link>
                </div>
                <div className="auth-input-wrap">
                  <Lock size={14} className="auth-input-icon" />
                  <input type={showPw ? 'text' : 'password'} name="password" value={form.password}
                    onChange={onChange} placeholder="••••••••"
                    className="auth-input auth-input-pw" disabled={loading}
                    autoComplete="current-password" required />
                  <button type="button" className="auth-eye" onClick={() => setShowPw(p => !p)} tabIndex={-1}>
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="auth-submit">
                {loading ? <span className="auth-spinner" /> : <>Sign in <ArrowRight size={14} /></>}
              </button>
            </form>

            <p className="auth-switch">
              Don't have an account?{' '}
              <Link to="/register" className="auth-switch-link">Create one →</Link>
            </p>
          </motion.div>
        </div>
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

/* ── Page shell ── */
.auth-page {
  display: flex; min-height: 100vh;
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
}

/* ── Left panel ── */
.auth-left {
  display: none; flex: 0 0 34%; position: relative; overflow: hidden;
  background: linear-gradient(175deg, #7c3aed 0%, #5b21b6 55%, #3b0f8e 100%);
}
@media (min-width: 860px) { .auth-left { display: flex; flex-direction: column; } }

.auth-left-inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; height: 100%; padding: 2rem 1.75rem;
}
.auth-brand {
  display: flex; align-items: center; gap: 0.5rem;
  text-decoration: none; font-size: 1.1rem; font-weight: 800; color: #fff; letter-spacing: -0.03em;
}
.auth-brand-icon {
  width: 1.875rem; height: 1.875rem; background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.25); border-radius: 0.4rem;
  display: flex; align-items: center; justify-content: center; color: #fff;
}
.auth-brand-accent { color: #ddd6fe; }
.auth-left-content {
  flex: 1; display: flex; flex-direction: column; justify-content: center;
}
.auth-eyebrow {
  display: inline-block; padding: 0.25rem 0.7rem; margin-bottom: 1rem;
  background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
  color: #e9d5ff; font-size: 0.7rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; border-radius: 999px;
}
.auth-left-heading {
  font-size: 1.35rem; font-weight: 800; color: #fff;
  line-height: 1.28; letter-spacing: -0.02em; margin-bottom: 0.75rem; text-wrap: balance;
}
.auth-left-sub {
  color: rgba(255,255,255,0.6); font-size: 0.82rem; line-height: 1.6; margin-bottom: 1.5rem;
}
.auth-features { display: flex; flex-direction: column; gap: 0.75rem; }
.auth-feature {
  display: flex; align-items: center; gap: 0.6rem;
  color: rgba(255,255,255,0.85); font-size: 0.82rem; font-weight: 500;
}
.auth-feature-icon {
  width: 1.75rem; height: 1.75rem; flex-shrink: 0;
  background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15);
  border-radius: 0.4rem; display: flex; align-items: center; justify-content: center; color: #ddd6fe;
}
.auth-blob { position: absolute; border-radius: 50%; filter: blur(70px); pointer-events: none; }
.auth-blob-1 { width: 300px; height: 300px; background: rgba(124,58,237,0.4); top: -80px; right: -80px; }
.auth-blob-2 { width: 220px; height: 220px; background: rgba(196,181,253,0.18); bottom: 40px; left: -50px; }

/* ── Right panel ── */
.auth-right {
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 3rem 4rem; background: #f5f5fa;
}
@media (max-width: 859px) { .auth-right { padding: 2rem 1.25rem; min-height: 100vh; } }

/* ── Form area (no card) ── */
.auth-card {
  width: 100%; max-width: 420px;
}

.auth-card-header { margin-bottom: 24px; }
.auth-card-title {
  font-size: 1.6rem; font-weight: 800; color: #14142b;
  letter-spacing: -0.03em; margin-bottom: 6px;
}
.auth-card-sub { font-size: 0.9rem; color: #6e6e86; }

/* ── Form elements ── */
.auth-error {
  background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca;
  border-radius: 10px; padding: 10px 14px; font-size: 0.85rem; margin-bottom: 16px;
}
.auth-field { margin-bottom: 14px; }
.auth-label {
  display: block; font-size: 0.8rem; font-weight: 600;
  color: #4a4a6a; margin-bottom: 6px; letter-spacing: 0.01em;
}
.auth-label-row {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;
}
.auth-label-row .auth-label { margin-bottom: 0; }
.auth-forgot { font-size: 0.8rem; color: #7c3aed; text-decoration: none; font-weight: 500; }
.auth-forgot:hover { text-decoration: underline; }

.auth-input-wrap { position: relative; }
.auth-input-icon {
  position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
  color: #a0a0bc; pointer-events: none;
}
.auth-input {
  width: 100%; padding: 11px 12px 11px 36px; font-size: 0.9rem;
  font-family: inherit; color: #14142b; background: #fff;
  border: 1.5px solid #ddddf0; border-radius: 10px; outline: none;
  transition: border-color 0.15s, box-shadow 0.15s; box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(20,20,60,0.06);
}
.auth-input:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124,58,237,0.10); }
.auth-input.error { border-color: #ef4444; }
.auth-input-pw { padding-right: 38px; }
.auth-eye {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: #a0a0bc; cursor: pointer;
  padding: 4px; display: flex; align-items: center;
}
.auth-eye:hover { color: #6e6e86; }

.auth-submit {
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 7px;
  padding: 12px 20px; margin-top: 8px;
  font-size: 0.9rem; font-weight: 700; font-family: inherit;
  color: #fff; background: #7c3aed; border: none; border-radius: 10px;
  cursor: pointer; box-shadow: 0 4px 16px rgba(124,58,237,0.32);
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
}
.auth-submit:hover:not(:disabled) {
  background: #6d28d9; transform: translateY(-1px); box-shadow: 0 8px 22px rgba(124,58,237,0.36);
}
.auth-submit:disabled { opacity: 0.65; cursor: not-allowed; }
.auth-spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  border-radius: 50%; animation: authspin 0.7s linear infinite; display: inline-block;
}
@keyframes authspin { to { transform: rotate(360deg); } }

.auth-switch {
  margin-top: 18px; text-align: center; font-size: 0.85rem; color: #6e6e86;
}
.auth-switch-link { color: #7c3aed; font-weight: 600; text-decoration: none; }
.auth-switch-link:hover { text-decoration: underline; }
`

export default Login
